<script setup lang="ts">
import InfoCircleIcon from '@/assets/svg/info-circle.svg?component';
import SparkleIcon from '@/assets/svg/sparkle.svg?component';

// Section heading for the dossier-derived filter block. A label, not a control: nothing here
// filters anything.
//
// Each filter carries its own "include candidates with no … information" checkbox, so the tooltip
// stays mode-neutral: it explains why that checkbox exists (sparse coverage, so a short list
// can mean "nobody told us"), not what any one filter does.
const TOOLTIP =
  'FactoryFix Insights come from prior candidate interactions and profile data. ' +
  "Most candidates haven't told us everything, so each filter shows whether it also keeps " +
  'candidates with no answer on file.';
</script>

<template>
  <div class="insights-head">
    <SparkleIcon class="insights-sparkle" viewBox="0 0 16 20" />
    <span class="insights-title">FactoryFix Insights</span>
    <!-- Same dark tooltip the card's insight chips use; its metrics are global
         (`.insights-chip-tooltip` in assets/css/tailwind.css) because v-tooltip teleports. -->
    <v-tooltip location="top" max-width="280px" content-class="insights-chip-tooltip">
      <template #activator="{ props: activatorProps }">
        <button
          v-bind="activatorProps"
          type="button"
          class="insights-info"
          aria-label="About FactoryFix Insights"
          data-test="smart-filters-insights-info"
        >
          <InfoCircleIcon viewBox="0 0 16 16" />
        </button>
      </template>
      {{ TOOLTIP }}
    </v-tooltip>
  </div>
</template>

<style scoped lang="postcss">
.insights-head {
  @apply mb-3 mt-5 flex items-center gap-1.5;
}

.insights-sparkle {
  @apply h-3.5 w-3.5 shrink-0 text-highlight-500;
}

.insights-title {
  @apply text-[11px] font-bold uppercase tracking-[0.06em] text-highlight-700;
}

.insights-info {
  @apply inline-flex shrink-0 items-center justify-center rounded-full text-tint-400 transition-colors;

  &:hover {
    @apply text-shade-800;
  }

  /* 14px, not the asset's native 16: info-circle.svg is drawn with stroke-width 2 on a 16
     viewBox, so at 16px it reads heavier than the design's icon and overpowers the sparkle
     beside it. 14px matches the sparkle and the design's optical weight. */
  svg {
    @apply h-3.5 w-3.5;
  }
}
</style>
